import {CLIENT_PORTAL_ORDERS_HOME} from 'app/app.constants';
import {FuseNavigation} from '@fuse/types';
import {customerNavigation} from 'app/navigation/customer-navigation';
import {
    CUSTOMER_DASHBOARD_ORDERS_THIS_WEEK_FEATURE,
    CUSTOMER_DASHBOARD_OUTSTANDING_PODS_FEATURE,
    CUSTOMER_DASHBOARD_SALES_REVENUE_FEATURE,
    CUSTOMER_DASHBOARD_UNBILLED_ORDERS_FEATURE
} from 'app/constants/role-settings/customer';

export const CLIENT_PORTAL_DASHBOARD_ID = 'client-portal-dashboard';
const DAILY_MANIFEST_ID = 'client-daily-manifest';

function flatten(items: FuseNavigation[]): FuseNavigation[] {
    return items.reduce((all, item) => all.concat(item.children ? flatten(item.children) : [item]), [] as FuseNavigation[]);
}

/** The customer's Dashboard switch is explicitly off in the role settings (absent = on). */
export function isCustomerDashboardOff(roleSettings: any): boolean {
    return !!roleSettings && roleSettings[CLIENT_PORTAL_DASHBOARD_ID] === false;
}

/** First customer menu page the role shows: the Daily Manifest when visible, else the first visible item, else the orders home. */
export function firstVisibleCustomerUrl(roleSettings: any, items: FuseNavigation[] = customerNavigation): string {
    const settings = roleSettings || {};
    const pages = flatten(items).filter((i) => i.url && i.url !== '/' && i.id !== CLIENT_PORTAL_DASHBOARD_ID);
    const manifest = pages.find((i) => i.id === DAILY_MANIFEST_ID);
    if (manifest && settings[DAILY_MANIFEST_ID] !== false) {
        return manifest.url;
    }
    const first = pages.find((i) => settings[i.id] === true);
    return first ? first.url : CLIENT_PORTAL_ORDERS_HOME;
}

/** Top-toolbar Add Order key: customers follow the Daily Manifest switch on every page, others by page. */
export function addOrderRoleKeyFor(url: string, isCustomer: boolean): string {
    return isCustomer || url.startsWith('/orders/daily-manifest')
        ? 'dailyManifest.pages.list.buttons.addOrder'
        : 'haulierOrders.pages.list.buttons.add';
}

/**
 * Where a customer must be sent instead of the Dashboard, or null to show it. Any failure to read the settings keeps the
 * Dashboard (the old behaviour) rather than breaking navigation.
 */
export async function customerLandingRedirect(isCustomer: boolean, loadSettings: () => Promise<any>): Promise<string | null> {
    if (!isCustomer) {
        return null;
    }
    try {
        const settings = await loadSettings();
        return isCustomerDashboardOff(settings) ? firstVisibleCustomerUrl(settings) : null;
    } catch {
        return null;
    }
}

const CLIENT_DASHBOARD_PANEL_FEATURES: Record<string, string> = {
    ordersThisWeek: CUSTOMER_DASHBOARD_ORDERS_THIS_WEEK_FEATURE,
    unbilledOrders: CUSTOMER_DASHBOARD_UNBILLED_ORDERS_FEATURE,
    outstandingPods: CUSTOMER_DASHBOARD_OUTSTANDING_PODS_FEATURE,
    salesRevenue: CUSTOMER_DASHBOARD_SALES_REVENUE_FEATURE
};

/** Customer dashboard panels start closed (CU-14zefhy38n2) unless the customer role turns that panel on. */
export function defaultClientDashboardState(roleSettings?: any): Record<string, string> {
    const state: Record<string, string> = {};
    for (const panel of Object.keys(CLIENT_DASHBOARD_PANEL_FEATURES)) {
        state[panel] = roleSettings?.[CLIENT_DASHBOARD_PANEL_FEATURES[panel]] === true ? 'show' : 'close';
    }
    return state;
}

/** Saved Display Panel rows override the defaults; unknown panels and rows without a state are ignored. */
export function applySavedPanelStates(defaults: Record<string, string>, rows: any[]): Record<string, string> {
    const result = {...defaults};
    for (const row of rows || []) {
        if (result[row.attributeName] !== undefined && row.state) {
            result[row.attributeName] = row.state;
        }
    }
    return result;
}
